import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileArchive,
  LayoutGrid,
  List,
  Package,
  Search,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import PageHeader from '@/components/feature/PageHeader'
import Card from '@/components/base/Card'
import { AnchorButton } from '@/components/base/Button'
import { mods, modCategories, type Mod, type ModCategory } from '@/data/mods'
import {
  vanillaPlugins,
  pluginCategories,
  type VanillaPlugin,
  type PluginCategory,
} from '@/data/vanillaPlugins'
import serverConfig from '@/config/serverConfig'
import { useLanguage } from '@/localization'

type ServerTab = 'vanilla' | 'modded'
type ViewMode = 'list' | 'cards'
type ActiveCategory = 'All' | ModCategory | PluginCategory

const ITEMS_PER_PAGE = 6
const LIST_BATCH_SIZE = 40

const modCategoryColors: Record<ModCategory, string> = {
  Tech: 'bg-sky/20 text-sky-deep border-sky-deep/40',
  Magic: 'bg-purple-100 text-purple-700 border-purple-300',
  World: 'bg-grass/20 text-grass-deep border-grass-dark/40',
  QoL: 'bg-gold/20 text-gold-dark border-gold/50',
  Performance: 'bg-stone/20 text-stone-dark border-stone/50',
  Adventure: 'bg-wood-light/25 text-wood-dark border-wood/40',
  Library: 'bg-slate-deep/10 text-slate-deep border-slate-deep/20',
  'Resource Pack': 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Shader: 'bg-indigo-100 text-indigo-800 border-indigo-300',
}

const pluginCategoryColors: Record<PluginCategory, string> = {
  Claims: 'bg-grass/20 text-grass-deep border-grass-dark/40',
  Permissions: 'bg-sky/20 text-sky-deep border-sky-deep/40',
  Economy: 'bg-gold/20 text-gold-dark border-gold/50',
  World: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  Auth: 'bg-purple-100 text-purple-700 border-purple-300',
  QoL: 'bg-wood-light/25 text-wood-dark border-wood/40',
  Library: 'bg-slate-deep/10 text-slate-deep border-slate-deep/20',
  Proxy: 'bg-indigo-100 text-indigo-800 border-indigo-300',
}

const contentTypeLabels = {
  Mods: 'Mods',
  'Resource Packs': 'Resource packs',
  Shaders: 'Shaders',
} as const

const viewOptions: Array<{ mode: ViewMode; icon: LucideIcon; label: string }> = [
  { mode: 'list', icon: List, label: 'List' },
  { mode: 'cards', icon: LayoutGrid, label: 'Cards' },
]

function parseServerTab(value: string | null): ServerTab {
  return value === 'modded' ? 'modded' : 'vanilla'
}

function formatBytes(bytes: number, unknownLabel: string) {
  if (!bytes) return unknownLabel
  const units = ['B', 'KB', 'MB', 'GB']
  let size = bytes
  let unit = 0

  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }

  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

function formatDate(value: string, locale: string, unknownLabel: string) {
  if (!value) return unknownLabel
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(value))
}

function ModThumb({ mod, size = 'md' }: { mod: Mod; size?: 'sm' | 'md' }) {
  const dimensions = size === 'sm' ? 'h-12 w-12' : 'h-14 w-14'

  if (!mod.thumbnailUrl) {
    return (
      <div
        className={`${dimensions} flex shrink-0 items-center justify-center rounded-lg border border-stone-light bg-grass/10 text-grass-deep`}
      >
        <Package size={size === 'sm' ? 20 : 24} />
      </div>
    )
  }

  return (
    <img
      src={mod.thumbnailUrl}
      alt=""
      loading="lazy"
      className={`${dimensions} shrink-0 rounded-lg border border-stone-light bg-white object-cover`}
    />
  )
}

function PluginThumb({ plugin, size = 'sm' }: { plugin: VanillaPlugin; size?: 'sm' | 'md' }) {
  const dimensions = size === 'sm' ? 'h-12 w-12' : 'h-14 w-14'

  if (!plugin.thumbnailUrl) {
    return (
      <div
        className={`${dimensions} flex shrink-0 items-center justify-center rounded-lg border border-stone-light bg-grass/10 text-grass-deep`}
      >
        <Package size={size === 'sm' ? 20 : 24} />
      </div>
    )
  }

  return (
    <img
      src={plugin.thumbnailUrl}
      alt=""
      loading="lazy"
      className={`${dimensions} shrink-0 rounded-lg border border-stone-light bg-white object-cover`}
    />
  )
}

function ModCategoryBadge({ mod }: { mod: Mod }) {
  const { t } = useLanguage()
  return (
    <span
      className={[
        'inline-flex shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-bold',
        modCategoryColors[mod.category],
      ].join(' ')}
    >
      {t(mod.category)}
    </span>
  )
}

function PluginCategoryBadge({ plugin }: { plugin: VanillaPlugin }) {
  const { t } = useLanguage()
  return (
    <span
      className={[
        'inline-flex shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-bold',
        pluginCategoryColors[plugin.category],
      ].join(' ')}
    >
      {t(plugin.category)}
    </span>
  )
}

function ModCard({ mod }: { mod: Mod }) {
  const { t, languageOption } = useLanguage()
  return (
    <Card className="flex flex-col">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold leading-tight text-slate-deep">{mod.name}</h3>
          <p className="truncate text-xs font-bold text-slate-deep/55">
            {t('by')} {mod.author}
          </p>
        </div>
        <ModCategoryBadge mod={mod} />
      </div>

      <div className="mb-3 flex items-center gap-3">
        <ModThumb mod={mod} size="sm" />
        <p className="line-clamp-3 text-sm text-slate-deep/70">{mod.blurb}</p>
      </div>

      <dl className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs text-slate-deep/70">
        <div>
          <dt className="font-bold text-slate-deep">{t('Type')}</dt>
          <dd>{t(contentTypeLabels[mod.contentType])}</dd>
        </div>
        <div>
          <dt className="font-bold text-slate-deep">{t('Size')}</dt>
          <dd>{formatBytes(mod.fileSizeBytes, t('Unknown size'))}</dd>
        </div>
        <div>
          <dt className="font-bold text-slate-deep">{t('Project ID')}</dt>
          <dd className="font-mono">{mod.projectId}</dd>
        </div>
        <div>
          <dt className="font-bold text-slate-deep">{t('File ID')}</dt>
          <dd className="font-mono">{mod.fileId}</dd>
        </div>
      </dl>

      <div className="mt-auto space-y-3">
        <div className="flex items-center gap-2 text-xs text-slate-deep/55">
          <CalendarDays size={14} />
          <span>{formatDate(mod.fileDate, languageOption.locale, t('Unknown date'))}</span>
          <span>-</span>
          <span>
            {mod.dependencyCount} {t('deps')}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-2 text-xs text-slate-deep/55">
          <FileArchive size={14} className="shrink-0" />
          <span className="truncate" title={mod.fileName}>
            {mod.fileName}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <a
            href={mod.downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-sky-deep hover:underline"
          >
            {t('File')} <Download size={12} />
          </a>
          <a
            href={mod.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-grass-deep hover:underline"
          >
            {t('Project')} <ExternalLink size={12} />
          </a>
        </div>
        <details className="text-xs text-slate-deep/60">
          <summary className="cursor-pointer font-bold text-slate-deep/70">
            {t('Versions and hashes')}
          </summary>
          <div className="mt-2 space-y-1 break-all">
            <p>{mod.gameVersions.join(', ')}</p>
            <p>SHA1: {mod.sha1 || 'n/a'}</p>
            <p>MD5: {mod.md5 || 'n/a'}</p>
          </div>
        </details>
      </div>
    </Card>
  )
}

function PluginCard({ plugin }: { plugin: VanillaPlugin }) {
  const { t } = useLanguage()
  return (
    <Card className="flex flex-col">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold leading-tight text-slate-deep">{plugin.name}</h3>
          <p className="truncate text-xs font-bold text-slate-deep/55" data-no-translate>
            {t('Version')} {plugin.version}
          </p>
        </div>
        <PluginCategoryBadge plugin={plugin} />
      </div>

      <div className="mb-4 flex items-center gap-3">
        <PluginThumb plugin={plugin} size="sm" />
        <p className="line-clamp-4 text-sm text-slate-deep/70">{t(plugin.blurb)}</p>
      </div>

      {plugin.url && (
        <div className="mt-auto pt-1">
          <a
            href={plugin.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs font-bold text-grass-deep hover:underline"
          >
            {t('Project')} <ExternalLink size={12} />
          </a>
        </div>
      )}
    </Card>
  )
}

function ModListView({ items }: { items: Mod[] }) {
  const { t, languageOption } = useLanguage()
  return (
    <div className="overflow-hidden rounded-xl border border-stone-light bg-white shadow-block">
      {items.map((mod) => (
        <div
          key={`${mod.projectId}-${mod.fileId}`}
          className="grid gap-3 border-b border-stone-light/70 p-3 last:border-b-0 md:grid-cols-[minmax(0,1fr)_180px_140px] md:items-center"
        >
          <div className="flex min-w-0 items-center gap-3">
            <ModThumb mod={mod} size="sm" />
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h3 className="truncate text-base font-extrabold text-slate-deep">{mod.name}</h3>
                <ModCategoryBadge mod={mod} />
              </div>
              <p className="truncate text-sm text-slate-deep/65">
                {t(contentTypeLabels[mod.contentType])} {t('by')} {mod.author}
              </p>
              <p className="mt-0.5 truncate text-xs text-slate-deep/45" title={mod.fileName}>
                {mod.fileName}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-slate-deep/65 sm:grid-cols-4 md:grid-cols-1">
            <span>
              <strong className="text-slate-deep">{t('Size')}:</strong>{' '}
              {formatBytes(mod.fileSizeBytes, t('Unknown size'))}
            </span>
            <span>
              <strong className="text-slate-deep">{t('Updated')}:</strong>{' '}
              {formatDate(mod.fileDate, languageOption.locale, t('Unknown date'))}
            </span>
            <span>
              <strong className="text-slate-deep">{t('Project')}:</strong> {mod.projectId}
            </span>
            <span>
              <strong className="text-slate-deep">{t('File')}:</strong> {mod.fileId}
            </span>
          </div>

          <div className="flex items-center gap-3 md:justify-end">
            <a
              href={mod.downloadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-sky-deep/20 px-2.5 py-1.5 text-xs font-bold text-sky-deep transition-colors hover:bg-sky/10"
            >
              {t('File')} <Download size={12} />
            </a>
            <a
              href={mod.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-grass-dark/20 px-2.5 py-1.5 text-xs font-bold text-grass-deep transition-colors hover:bg-grass/10"
            >
              {t('Project')} <ExternalLink size={12} />
            </a>
          </div>
        </div>
      ))}
    </div>
  )
}

function PluginListView({ items }: { items: VanillaPlugin[] }) {
  const { t } = useLanguage()
  return (
    <div className="overflow-hidden rounded-xl border border-stone-light bg-white shadow-block">
      {items.map((plugin) => (
        <div
          key={`${plugin.name}-${plugin.version}`}
          className="grid gap-3 border-b border-stone-light/70 p-3 last:border-b-0 md:grid-cols-[minmax(0,1fr)_160px_120px] md:items-center"
        >
          <div className="flex min-w-0 items-center gap-3">
            <PluginThumb plugin={plugin} size="sm" />
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <h3 className="truncate text-base font-extrabold text-slate-deep">{plugin.name}</h3>
                <PluginCategoryBadge plugin={plugin} />
              </div>
              <p className="mt-0.5 line-clamp-2 text-sm text-slate-deep/65">{t(plugin.blurb)}</p>
            </div>
          </div>

          <div className="text-xs text-slate-deep/65">
            <strong className="text-slate-deep">{t('Version')}:</strong>{' '}
            <span data-no-translate>{plugin.version}</span>
          </div>

          <div className="flex items-center gap-3 md:justify-end">
            {plugin.url ? (
              <a
                href={plugin.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-lg border border-grass-dark/20 px-2.5 py-1.5 text-xs font-bold text-grass-deep transition-colors hover:bg-grass/10"
              >
                {t('Project')} <ExternalLink size={12} />
              </a>
            ) : (
              <span className="text-xs text-slate-deep/40">—</span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default function ModsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const serverTab = parseServerTab(searchParams.get('server'))
  const isVanilla = serverTab === 'vanilla'

  const [query, setQuery] = useState('')
  const [active, setActive] = useState<ActiveCategory>('All')
  const [page, setPage] = useState(1)
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [visibleListCount, setVisibleListCount] = useState(LIST_BATCH_SIZE)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)
  const { modpack } = serverConfig.servers.modded
  const { t } = useLanguage()

  function setServerTab(next: ServerTab) {
    setSearchParams(next === 'vanilla' ? { server: 'vanilla' } : { server: 'modded' }, {
      replace: false,
    })
  }

  useEffect(() => {
    setQuery('')
    setActive('All')
    setPage(1)
    setVisibleListCount(LIST_BATCH_SIZE)
  }, [serverTab])

  const modCounts = useMemo(
    () =>
      mods.reduce(
        (acc, mod) => {
          acc.total += 1
          acc[mod.contentType] += 1
          return acc
        },
        { total: 0, Mods: 0, 'Resource Packs': 0, Shaders: 0 },
      ),
    [],
  )

  const filteredMods = useMemo(() => {
    const q = query.trim().toLowerCase()
    return mods.filter((mod) => {
      const matchCat = active === 'All' || mod.category === active
      const searchable = [
        mod.name,
        mod.author,
        mod.fileName,
        mod.contentType,
        mod.category,
        mod.gameVersions.join(' '),
        String(mod.projectId),
        String(mod.fileId),
        mod.blurb,
      ]
        .join(' ')
        .toLowerCase()

      return matchCat && (!q || searchable.includes(q))
    })
  }, [query, active])

  const filteredPlugins = useMemo(() => {
    const q = query.trim().toLowerCase()
    return vanillaPlugins.filter((plugin) => {
      const matchCat = active === 'All' || plugin.category === active
      const searchable = [plugin.name, plugin.version, plugin.category, plugin.blurb]
        .join(' ')
        .toLowerCase()
      return matchCat && (!q || searchable.includes(q))
    })
  }, [query, active])

  const filtered = isVanilla ? filteredPlugins : filteredMods
  const categories = isVanilla ? pluginCategories : modCategories

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE))
  const currentPage = Math.min(page, totalPages)
  const pageStart = filtered.length === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1
  const pageEnd = Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  )
  const visibleListItems = filtered.slice(0, visibleListCount)
  const hasMoreListItems = viewMode === 'list' && visibleListCount < filtered.length
  const itemWord = isVanilla ? t('matching plugins') : t('matching items')
  const resultLabel =
    viewMode === 'list'
      ? `${t('Loaded')} ${Math.min(visibleListCount, filtered.length)} ${t('of')} ${filtered.length} ${itemWord}`
      : `${t('Showing')} ${pageStart}-${pageEnd} ${t('of')} ${filtered.length} ${itemWord}`

  useEffect(() => {
    setVisibleListCount(LIST_BATCH_SIZE)
  }, [active, query, viewMode, serverTab])

  useEffect(() => {
    if (viewMode !== 'list' || !hasMoreListItems) return

    const node = loadMoreRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisibleListCount((count) => Math.min(count + LIST_BATCH_SIZE, filtered.length))
        }
      },
      { rootMargin: '600px 0px' },
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [filtered.length, hasMoreListItems, viewMode, visibleListCount])

  function goToPage(nextPage: number) {
    setPage(Math.min(Math.max(nextPage, 1), totalPages))
  }

  return (
    <>
      <PageHeader
        title={isVanilla ? 'Server Plugins' : 'The Modpack'}
        subtitle={
          isVanilla
            ? 'Paper plugins plus Velocity auth for Uliunai Survival — no client modpack required.'
            : 'The real Uliunai CurseForge profile: 236 mods, 11 resource packs and 2 shader packs for NeoForge 1.21.1.'
        }
      />

      <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="mb-8 flex justify-center">
          <div
            className="inline-flex rounded-xl border border-stone-light bg-white p-1 shadow-block"
            role="tablist"
            aria-label={t('Server')}
          >
            {(
              [
                { id: 'vanilla' as const, label: 'Survival' },
                { id: 'modded' as const, label: 'Modded' },
              ] as const
            ).map(({ id, label }) => {
              const selected = serverTab === id
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setServerTab(id)}
                  className={[
                    'inline-flex h-11 min-w-36 items-center justify-center rounded-lg px-4 text-sm font-extrabold transition-colors',
                    selected
                      ? id === 'vanilla'
                        ? 'bg-grass-dark text-white'
                        : 'bg-sky-deep text-white'
                      : 'text-slate-deep/65 hover:bg-grass/10 hover:text-grass-deep',
                  ].join(' ')}
                >
                  {t(label)}
                </button>
              )
            })}
          </div>
        </div>

        {!isVanilla && modpack && (
          <Card variant="sky" className="mb-10">
            <div className="flex flex-col gap-6 md:flex-row md:items-center">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-sky-deep text-white shadow-block">
                <Package size={32} />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-extrabold text-slate-deep">{modpack.name}</h2>
                <p className="text-slate-deep/70">
                  {t('Version')} {modpack.version} {t('via')} {modpack.launcher} {t('for Minecraft')}{' '}
                  {serverConfig.servers.modded.version}
                </p>
                <p className="mt-1 text-sm text-slate-deep/70">
                  {t('Local CurseForge profile export with')} {modCounts.total}{' '}
                  {t('content items. Install the shared pack through CurseForge.')}
                </p>
              </div>
              <AnchorButton href={modpack.url} target="_blank" rel="noreferrer" variant="sky">
                <Download size={18} /> {t('Get CurseForge')}
              </AnchorButton>
            </div>
          </Card>
        )}

        {isVanilla ? (
          <div className="mb-10 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {[
              [
                'Plugins',
                vanillaPlugins.filter((p) => p.category !== 'Auth' && p.category !== 'Proxy')
                  .length,
              ],
              [
                'Proxy / Auth',
                vanillaPlugins.filter((p) => p.category === 'Auth' || p.category === 'Proxy')
                  .length,
              ],
              ['Categories', pluginCategories.length],
            ].map(([label, value]) => (
              <Card key={String(label)} className="text-center">
                <div className="text-3xl font-extrabold text-grass-deep">{value}</div>
                <div className="text-sm font-bold text-slate-deep/60">{t(String(label))}</div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="mb-10 grid gap-4 md:grid-cols-4">
            {[
              ['Content', modCounts.total],
              ['Mods', modCounts.Mods],
              ['Resource packs', modCounts['Resource Packs']],
              ['Shaders', modCounts.Shaders],
            ].map(([label, value]) => (
              <Card key={String(label)} className="text-center">
                <div className="text-3xl font-extrabold text-grass-deep">{value}</div>
                <div className="text-sm font-bold text-slate-deep/60">{t(String(label))}</div>
              </Card>
            ))}
          </div>
        )}

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-deep/40"
            />
            <input
              type="text"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setPage(1)
              }}
              placeholder={t(
                isVanilla
                  ? 'Search by plugin name, category or description...'
                  : 'Search by mod, author, file name, project ID, file ID or version...',
              )}
              className="w-full rounded-xl border border-stone-light bg-white py-2.5 pl-10 pr-4 text-slate-deep placeholder:text-slate-deep/40 focus:border-grass-dark focus:outline-none focus:ring-2 focus:ring-grass/30"
            />
          </div>

          <div className="inline-flex rounded-xl border border-stone-light bg-white p-1 shadow-block">
            {viewOptions.map(({ mode, icon: Icon, label }) => {
              const activeMode = viewMode === mode
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode as ViewMode)}
                  className={[
                    'inline-flex h-10 min-w-24 items-center justify-center gap-2 rounded-lg px-3 text-sm font-extrabold transition-colors',
                    activeMode
                      ? 'bg-grass-dark text-white'
                      : 'text-slate-deep/65 hover:bg-grass/10 hover:text-grass-deep',
                  ].join(' ')}
                >
                  <Icon size={16} />
                  {t(label)}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mb-8 flex flex-wrap gap-2">
          {(['All', ...categories] as const).map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setActive(cat)
                setPage(1)
              }}
              className={[
                'cursor-pointer rounded-full border px-4 py-1.5 text-sm font-bold transition-colors',
                active === cat
                  ? 'border-grass-deep bg-grass-dark text-white'
                  : 'border-stone-light bg-white text-slate-deep/70 hover:border-grass-dark/50',
              ].join(' ')}
            >
              {t(cat)}
            </button>
          ))}
        </div>

        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-bold text-slate-deep/60">{resultLabel}</p>
          <p className="text-xs text-slate-deep/50">
            {isVanilla
              ? t('Pinned from minecraft/server/versions.env — update the catalog when pins change.')
              : t('Data generated from the installed CurseForge profile on June 15, 2026.')}
          </p>
        </div>

        {filtered.length === 0 ? (
          <p className="py-12 text-center text-slate-deep/60">
            {t(
              isVanilla
                ? 'No plugins match your search. Try another keyword or category.'
                : 'No mods match your search. Try another keyword or category.',
            )}
          </p>
        ) : viewMode === 'list' ? (
          <>
            {isVanilla ? (
              <PluginListView items={visibleListItems as VanillaPlugin[]} />
            ) : (
              <ModListView items={visibleListItems as Mod[]} />
            )}
            {hasMoreListItems && (
              <div ref={loadMoreRef} className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() =>
                    setVisibleListCount((count) =>
                      Math.min(count + LIST_BATCH_SIZE, filtered.length),
                    )
                  }
                  className="rounded-xl border border-stone-light bg-white px-5 py-2.5 text-sm font-extrabold text-grass-deep shadow-block transition-colors hover:border-grass-dark/50 hover:bg-grass/10"
                >
                  {t(isVanilla ? 'Load more plugins' : 'Load more mods')}
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {isVanilla
                ? (paginated as VanillaPlugin[]).map((plugin) => (
                    <PluginCard key={`${plugin.name}-${plugin.version}`} plugin={plugin} />
                  ))
                : (paginated as Mod[]).map((mod) => (
                    <ModCard key={`${mod.projectId}-${mod.fileId}`} mod={mod} />
                  ))}
            </div>

            <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-xl border border-stone-light bg-white p-4 shadow-block sm:flex-row">
              <p className="text-sm font-bold text-slate-deep/65">
                {t('Page')} {currentPage} {t('of')} {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="inline-flex h-10 items-center gap-1 rounded-lg border border-stone-light bg-white px-3 text-sm font-bold text-slate-deep transition-colors hover:border-grass-dark/50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft size={16} />
                  {t('Prev')}
                </button>
                <span className="min-w-24 rounded-lg bg-grass/10 px-3 py-2 text-center text-sm font-extrabold text-grass-deep">
                  {pageStart}-{pageEnd}
                </span>
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex h-10 items-center gap-1 rounded-lg border border-stone-light bg-white px-3 text-sm font-bold text-slate-deep transition-colors hover:border-grass-dark/50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {t('Next')}
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
